import classNames from 'classnames';
import React, { useState } from 'react';
import Select from 'react-select';
import { ReactSVG } from 'react-svg';
import Tooltip from 'react-tooltip-lite';
import type { BookingHost } from 'types/booking';
import { useTranslation } from 'utils/i18n';
import {
  DESKTOP_TOOLTIP_DISTANCE,
  SHARED_TOOLTIP_DISTANCE,
} from '../../constants/breakpoints';
import { selectStyles } from '../../constants/select';
import useIsMobile from '../../hooks/useIsMobile';

interface Props {
  bookingHosts: Array<BookingHost>;
  bookingHostId?: number;
  showCreateNewOption?: boolean;
  showToolTip?: boolean;
  hasError?: boolean;
  onSelectNew?: () => void;
  setBookingHostId?: (id: number) => void;
  removeBookingHostId?: () => void;
  disabled: boolean;
  value?: HostOption | null;
}

// The option shape the select renders: the placeholder row has no id and the
// synthetic "add new host" row carries the '__new' sentinel.
export interface HostOption {
  value?: number | string;
  label?: string;
}

const HostSelect: React.FC<Props> = ({
  bookingHosts = [],
  bookingHostId,
  showCreateNewOption = false,
  showToolTip = true,
  hasError = false,
  onSelectNew = () => {},
  setBookingHostId = () => {},
  removeBookingHostId = () => {},
  disabled: isDisabled = false,
  value: valueProp,
  ...props
}) => {
  const { t } = useTranslation('common');
  const NEW_FIELD = {
    value: '__new',
    label: t('quick_book.add_new_host'),
  };
  const [newFieldSelected, selectNewField] = useState(false);

  const isMobileView = useIsMobile();

  // null (rather than the old `false`) is react-select's own "no selection"
  // value; both are treated identically at runtime.
  const selectedBookingHost = (): HostOption | null => {
    if (valueProp !== undefined) {
      return valueProp?.value ? valueProp : null;
    }

    if (newFieldSelected) {
      return NEW_FIELD;
    }

    if (bookingHostId) {
      const bookingHost =
        bookingHosts.find(host => host.id === bookingHostId) ||
        ({} as BookingHost);

      return {
        value: bookingHostId,
        label: bookingHost.label,
      };
    }

    return null;
  };

  const classes = classNames('field', 'field--select-list', {
    'field--has-error': hasError,
  });

  return (
    <div
      className="field__wrapper"
      data-cy="bookingHost"
      data-testid="bookingHostSelect"
    >
      <Select<HostOption, false>
        placeholder={t('shared.select_host')}
        name="booking_host_id"
        styles={selectStyles}
        isSearchable
        isClearable
        isDisabled={isDisabled}
        className={classes}
        options={[
          {
            value: undefined,
            label: t('shared.select_host'),
          },
          ...bookingHosts
            .filter(host => host.active === true)
            .map(bookingHost => ({
              value: bookingHost.id,
              label: bookingHost.label,
            })),
          ...(showCreateNewOption ? [NEW_FIELD] : []),
        ]}
        onChange={(bookingHost, action) => {
          switch (action.action) {
            case 'select-option': {
              if (bookingHost?.value === NEW_FIELD.value) {
                selectNewField(true);
                return onSelectNew();
              }
              selectNewField(false);
              // The placeholder row's value is undefined; passing it through
              // matches what this handler always did.
              return setBookingHostId(bookingHost?.value as number);
            }

            case 'clear': {
              return removeBookingHostId();
            }

            default: {
              return false;
            }
          }
        }}
        onFocus={() => removeBookingHostId()}
        value={selectedBookingHost()}
        {...props}
      />

      {showToolTip && (
        <Tooltip
          content={t('quick_book.help_text')}
          className="quick-book__tooltip"
          distance={
            isMobileView ? SHARED_TOOLTIP_DISTANCE : DESKTOP_TOOLTIP_DISTANCE
          }
        >
          <ReactSVG
            src="/images/icons/blue-question-mark.svg"
            width={17}
            height={17}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default HostSelect;
